/**
 * ExcludeGroupCard — one rule, read top to bottom as a sentence:
 *   Rule 1
 *   On <layers> when <ALL|ANY> of these match:
 *     <condition>  and  <condition>
 *   → then <actions>
 *   Optional: <date range> <skip>
 *
 * For Media Devoted | Created By Ciro Autiero
 *
 * Nesting still tops out at one level (NOT); there is no "+ Group" inside.
 * This file is only the shell — each clause lives in `group/`.
 */

import { FC } from 'react';
import type { FlowLayer, FormulaGroup, RuleAction } from '@trafficpulse/shared';

import { excludeGroupWindowKey } from '../../compare/live/inherit-key';
import ExcludeGroupDateRange, { groupUsesDateWindow } from './ExcludeGroupDateRange';
import {
  RuleCardHeader,
  RuleConditionsList,
  RuleConsequence,
  RuleQuietAdders,
  RuleSentence,
} from './group';
import type { WeightIssue } from './group/weight';
import { useGroupTags } from './use-group-tags';
import {
  makeCondition,
  makeNot,
  type ExcludeViewGroup,
  type ExcludeViewItem,
} from '../../utils/exclude-view-model';

export interface ExcludeGroupCardProps {
  group: ExcludeViewGroup;
  onChange: (next: ExcludeViewGroup) => void;
  onRemove: () => void;
  /** Live formula groups — drives the "inherits X" default-range hint. */
  formulaGroups: FormulaGroup[];
  /** 1-based position, for the unnamed group's "Rule N" fallback. */
  index: number;
  /** Weight-action problem to show on this card (error or warning). */
  weightIssue?: WeightIssue;
}

const ExcludeGroupCard: FC<ExcludeGroupCardProps> = ({
  group,
  onChange,
  onRemove,
  formulaGroups,
  index,
  weightIssue,
}) => {
  const patch = (next: Partial<ExcludeViewGroup>) => onChange({ ...group, ...next });
  // Ticking Weight for the first time seeds a sensible payload, so the card
  // never shows an action with empty numbers.
  const patchActions = (actions: RuleAction[]) =>
    patch({
      actions,
      ...(actions.includes('weight') && !group.weight
        ? { weight: { mode: 'static' as const, percent: 20 } }
        : {}),
    });
  const removeItem = (id: string) =>
    patch({ children: group.children.filter((c) => c.id !== id) });
  const replaceItem = (next: ExcludeViewItem) =>
    patch({
      children: group.children.map((c) => (c.id === next.id ? next : c)),
    });

  // Ranking scope defaults to the group's first layer; tags are unioned across
  // every layer the group applies to. A multi-layer group uses the first for
  // scope — an acceptable simplification (most groups target one layer).
  const layer = group.appliesTo[0] ?? 'landers';
  const tags = useGroupTags(group.appliesTo);

  return (
    <article className="space-y-3 rounded-lg border border-border bg-card p-3.5 shadow-sm">
      <RuleCardHeader
        name={group.name}
        index={index}
        onRename={(name) => patch({ name })}
        onRemove={onRemove}
      />
      <RuleSentence
        appliesTo={group.appliesTo}
        op={group.op}
        onScopeChange={(appliesTo: ReadonlyArray<FlowLayer>) =>
          patch({ appliesTo: [...appliesTo] })
        }
        onOpChange={(op) => patch({ op })}
      />
      <RuleConditionsList
        items={group.children}
        op={group.op}
        layer={layer}
        windowKey={excludeGroupWindowKey(group, formulaGroups, layer)}
        tags={tags}
        onReplace={replaceItem}
        onRemove={removeItem}
        onAddCondition={() => patch({ children: [...group.children, makeCondition()] })}
        onAddNot={() => patch({ children: [...group.children, makeNot()] })}
        footer={
          <div className="flex flex-wrap items-center gap-2">
            {groupUsesDateWindow(group) ? (
              <span className="text-[12.5px] font-semibold text-muted-foreground">
                measured over
              </span>
            ) : null}
            <ExcludeGroupDateRange
              group={group}
              onChange={onChange}
              formulaGroups={formulaGroups}
            />
          </div>
        }
      />
      <RuleConsequence
        actions={group.actions}
        appliesTo={group.appliesTo}
        onChange={patchActions}
        weight={group.weight}
        onWeightChange={(weight) => patch({ weight })}
        weightIssue={weightIssue}
      />
      <RuleQuietAdders group={group} onChange={onChange} />
    </article>
  );
};

export default ExcludeGroupCard;
